import Image from "next/image";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import { useState } from "react";
import classNames from "classnames";
import { Modal } from "@mantine/core";
import { addBasePathToStrapiMedia, StrapiImage } from "@/utils/strapi";
import { Reservation } from "@/components/pages/account/_helpers/useFetchUserReservations";
import { CrossIcon } from "@/public/icons/CrossIcon";
import { ResumeBlock } from "./resumeBlock/ResumeBlock";
import { UtilsBlock } from "./utilsBlock/UtilsBlock";
import { ContractBlock } from "./contractBlock/ContractBlock";
import styles from "./LocationDetails.module.scss";

type LocationDetailsProps = {
  isModalOpen: boolean;
  setIsOpened: (isOpened: boolean) => void;
  reservation: Reservation;
  categoryImage: StrapiImage;
};

type DetailsTabType = "resume" | "utils" | "contract";

export const LocationDetails = ({
  isModalOpen,
  setIsOpened,
  reservation,
  categoryImage,
}: LocationDetailsProps) => {
  const intl = useIntl();
  const tabs: DetailsTabType[] = ["utils", "resume" /*, "contract"*/];
  const [selectedTab, setSelectedTab] = useState<DetailsTabType>("utils");

  return (
    <Modal
      opened={isModalOpen}
      centered
      withCloseButton={false}
      onClose={() => setIsOpened(false)}
      transition="fade"
      classNames={{
        modal: styles.modal,
        overlay: styles.modalOverlay,
        body: styles.modalBody,
        root: styles.modalRoot,
        inner: styles.modalInner,
      }}
    >
      <div className={styles.closeModal} onClick={() => setIsOpened(false)}>
        <CrossIcon />
      </div>

      <div className={styles.header}>
        <div className={styles.vehicleImage}>
          <Image
            src={addBasePathToStrapiMedia(categoryImage)}
            alt={categoryImage.alternativeText ?? ""}
            layout="fill"
            objectFit="contain"
          />
        </div>
        <div className={styles.mainInfos}>
          <div className={styles.title}>
            {reservation.bookingId &&
              `${intl.formatMessage({ id: "reservation" })} ${
                reservation.bookingId
              }`}
            {!reservation.bookingId && intl.formatMessage({ id: "quote" })}
          </div>
          <div className={styles.dates}>
            <span className={styles.bold}>
              {reservation.travel.startAgency.name}
            </span>
            {dayjs(reservation.travel.startDate).format("(DD/MM/YYYY)")}
            <span className={styles.separator}>-</span>
            <span className={styles.bold}>
              {reservation.travel.endAgency.name}
            </span>
            {dayjs(reservation.travel.endDate).format("(DD/MM/YYYY)")}
          </div>
        </div>
      </div>

      <div className={styles.tabSelection}>
        {tabs.map((tab, index) => (
          <div
            className={classNames(styles.tab, {
              [styles.active]: tab === selectedTab,
            })}
            key={index}
            onClick={() => setSelectedTab(tab)}
          >
            {intl.formatMessage({ id: tab })}
          </div>
        ))}
      </div>

      <div className={styles.currentTab}>
        {selectedTab === "resume" && <ResumeBlock reservation={reservation} />}
        {selectedTab === "utils" && <UtilsBlock reservation={reservation} />}
        {selectedTab === "contract" && <ContractBlock />}
      </div>
    </Modal>
  );
};
